/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { getApiBaseUrl } from '../../../../../core/config/api';
import type { TabKey } from './useModerationManageState';

interface UseModerationActionsOpts {
  userId: number;
  canManagePremium: boolean;
  tab: TabKey;
  editForm: Record<string, string>;
  setEditForm: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  editingId: number | null;
  setEditingId: React.Dispatch<React.SetStateAction<number | null>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  vehicleStatusCodeById: Map<number, string>;
  vehicleStatusCodeByName: Map<string, string>;
  playerProfile: any;
  setSavingProfile: React.Dispatch<React.SetStateAction<boolean>>;
  economyAction: any;
  setEconomyAction: React.Dispatch<React.SetStateAction<any>>;
  setEconomyAdjusting: React.Dispatch<React.SetStateAction<boolean>>;
  generatedScenarioId: string;
  load: () => Promise<void>;
  loadProfile: () => Promise<void>;
  loadCtaBuildings: () => Promise<void>;
}

/** Hook extrait de useModerationManage : tous les handlers d'action
 *  (delete/save/economy/profile/cta/generation). */
export function useModerationManageActions(opts: UseModerationActionsOpts) {
  const {
    userId,
    canManagePremium,
    tab,
    editForm,
    setEditForm,
    setEditingId,
    setError,
    vehicleStatusCodeById,
    vehicleStatusCodeByName,
    playerProfile,
    setSavingProfile,
    economyAction,
    setEconomyAction,
    setEconomyAdjusting,
    generatedScenarioId,
    load,
    loadProfile,
    loadCtaBuildings,
  } = opts;

  const handleDelete = async (t: TabKey, ownerId: number, id: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');
      const res = await fetch(`${getApiBaseUrl(true)}/panel/v1/moderation/possessions/${ownerId}/${t}/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Erreur suppression');
      setEditingId(null);
      setEditForm({});
      await load();
    } catch (e: any) {
      setError(e?.message || 'Erreur suppression');
    }
  };

  const callAction = async (url: string, method: 'PATCH' | 'POST', body?: Record<string, unknown>) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Token manquant');
    const res = await fetch(url, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
    });
    const data = await res.json();
    if (!res.ok || !data?.success) throw new Error(data?.error || 'Action impossible');
  };

  const startEdit = (item: any) => {
    setEditingId(Number(item.id));
    const next: Record<string, string> = {};
    if (tab === 'buildings') {
      next.nom = String(item.nom || '');
      next.niveau = String(item.niveau || 1);
    } else if (tab === 'vehicles') {
      next.nom = String(item.nom || '');
      next.capacite = String(item.capacite ?? 0);
      next.kilometrage = String(item.kilometrage ?? 0);
      next.vehicle_status_id = String(item.vehicle_status_id || '');
      next.batiment_id = String(item.batiment_id || '');
      next.premium = String(Number(item.premium || 0));
      next.cout = String(item.cout ?? 0);
      next.config_vehicle_id = String(item.config_vehicle_id || '');
    } else if (tab === 'staff') {
      next.prenom = String(item.prenom || '');
      next.nom = String(item.nom || '');
      next.config_personnel_id = String(item.config_personnel_id || '');
      next.staff_status_id = String(item.staff_status_id || '');
      next.staff_status_code = String(
        vehicleStatusCodeById.get(Number(item.vehicle_status_id || item.staff_status_id || 0)) ||
          vehicleStatusCodeByName.get(String(item.staff_status_nom || '').trim().toLowerCase()) ||
          vehicleStatusCodeById.get(Number(item.staff_status_id || 0)) ||
          '',
      );
      next.planning_statut_nom = String(item.planning_statut_nom || '');
      next.batiment_id = String(item.batiment_id || '');
      next.actif = String(Number(item.actif ?? 1));
      next.premium = String(Number(item.premium || 0));
      next.groupe = String(item.groupe || '');
    } else {
      next.numero_intervention = String(item.numero_intervention || '');
      next.adresse = String(item.adresse || '');
      next.statut = String(item.statut || 'DISPONIBLE');
    }
    setEditForm(next);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleSave = async (ownerId: number, item: any) => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');
      const id = Number(item?.id || 0);
      const base = `${getApiBaseUrl(true)}/panel/v1/moderation/possessions/${ownerId}`;

      if (tab === 'buildings') {
        const res = await fetch(`${base}/buildings/${id}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ nom: editForm.nom || item.nom, niveau: Number(editForm.niveau || 1) }),
        });
        const data = await res.json();
        if (!res.ok || !data?.success) throw new Error(data?.error || 'Erreur sauvegarde');
      } else if (tab === 'vehicles') {
        const body: Record<string, unknown> = {
          nom: (editForm.nom || '').trim() || item.nom,
          capacite: Number(editForm.capacite ?? 0),
          kilometrage: Number(editForm.kilometrage ?? 0),
          vehicle_status_id: Number(editForm.vehicle_status_id || 0) || undefined,
          batiment_id: editForm.batiment_id === '' ? 0 : Number(editForm.batiment_id || 0),
          premium: Number(editForm.premium || 0) ? 1 : 0,
          cout: Number(editForm.cout ?? 0),
        };
        const cvId = Number(editForm.config_vehicle_id);
        if (Number.isFinite(cvId) && cvId > 0) body.config_vehicle_id = cvId;
        const res = await fetch(`${base}/vehicles/${id}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok || !data?.success) throw new Error(data?.error || 'Erreur sauvegarde');
      } else if (tab === 'staff') {
        const body: Record<string, unknown> = {
          prenom: (editForm.prenom || '').trim(),
          nom: (editForm.nom || '').trim(),
          planning_statut_nom: (editForm.planning_statut_nom || '').trim(),
          staff_status_code: (editForm.staff_status_code || '').trim().toLowerCase() || undefined,
          batiment_id: editForm.batiment_id === '' ? 0 : Number(editForm.batiment_id || 0),
          config_personnel_id: Number(editForm.config_personnel_id || 0) || undefined,
          actif: Number(editForm.actif || 0) ? 1 : 0,
          premium: Number(editForm.premium || 0) ? 1 : 0,
          groupe: (editForm.groupe || '').trim() || null,
        };
        const res = await fetch(`${base}/staff/${id}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok || !data?.success) throw new Error(data?.error || 'Erreur sauvegarde');
      } else {
        if (!String(editForm.adresse || '').trim()) throw new Error('Adresse requise pour intervention');
        const res = await fetch(`${base}/interventions/${id}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            numero_intervention: (editForm.numero_intervention || '').trim(),
            adresse: editForm.adresse,
            statut: editForm.statut,
          }),
        });
        const data = await res.json();
        if (!res.ok || !data?.success) throw new Error(data?.error || 'Erreur sauvegarde');
      }

      setEditingId(null);
      setEditForm({});
      await load();
    } catch (e: any) {
      setError(e?.message || 'Erreur sauvegarde');
    }
  };

  const savePlayerProfile = async () => {
    if (!playerProfile) return;
    try {
      setSavingProfile(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');
      const { premium: _premium, argents: _argents, medailles: _medailles, xp: _xp, ...profileSansEconomie } =
        playerProfile;
      const payload = canManagePremium ? playerProfile : profileSansEconomie;
      const res = await fetch(`${getApiBaseUrl(true)}/panel/v1/moderation/player/${userId}/profile`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Erreur sauvegarde profil');
      await loadProfile();
    } catch (e: any) {
      setError(e?.message || 'Erreur profil');
    } finally {
      setSavingProfile(false);
    }
  };

  const adjustEconomy = async (resource: 'argents' | 'medailles', operation: 'add' | 'remove') => {
    try {
      if (!playerProfile) return;
      setError(null);
      const amount = Math.floor(Number(economyAction[resource].amount || 0));
      if (!Number.isFinite(amount) || amount <= 0) {
        throw new Error('Montant invalide (doit être > 0)');
      }
      setEconomyAdjusting(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');
      const res = await fetch(`${getApiBaseUrl(true)}/panel/v1/moderation/player/${userId}/economy-adjust`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resource,
          operation,
          amount,
          reason: economyAction[resource].reason || '',
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Erreur ajustement économie');
      setEconomyAction((prev: any) => ({
        ...prev,
        [resource]: { ...prev[resource], amount: '', reason: '' },
      }));
      await loadProfile();
    } catch (e: any) {
      setError(e?.message || 'Erreur ajustement économie');
    } finally {
      setEconomyAdjusting(false);
    }
  };

  const toggleBuildingActive = async (buildingId: number, nextActif: boolean) => {
    try {
      await callAction(
        `${getApiBaseUrl(true)}/panel/v1/moderation/possessions/${userId}/buildings/${buildingId}/toggle-active`,
        'PATCH',
        { actif: nextActif ? 1 : 0 },
      );
      await load();
    } catch (e: any) {
      setError(e?.message || 'Erreur activation bâtiment');
    }
  };

  const updateBuildingCta = async (buildingId: number, ctaIdValue: string) => {
    try {
      await callAction(
        `${getApiBaseUrl(true)}/panel/v1/moderation/possessions/${userId}/buildings/${buildingId}/cta-link`,
        'PATCH',
        { cta_id: Number(ctaIdValue || 0) || null },
      );
      await load();
      await loadCtaBuildings();
    } catch (e: any) {
      setError(e?.message || 'Erreur liaison CTA');
    }
  };

  const resetIntervention = async (interventionId: number) => {
    try {
      await callAction(
        `${getApiBaseUrl(true)}/panel/v1/moderation/possessions/${userId}/interventions/${interventionId}/reset`,
        'PATCH',
      );
      await load();
    } catch (e: any) {
      setError(e?.message || 'Erreur reset intervention');
    }
  };

  const cancelIntervention = async (interventionId: number) => {
    try {
      await callAction(
        `${getApiBaseUrl(true)}/panel/v1/moderation/possessions/${userId}/interventions/${interventionId}/cancel`,
        'PATCH',
      );
      await load();
    } catch (e: any) {
      setError(e?.message || 'Erreur annulation intervention');
    }
  };

  const generateFromScenario = async () => {
    try {
      if (!generatedScenarioId) {
        setError('Scénario requis');
        return;
      }
      setError(null);
      await callAction(
        `${getApiBaseUrl(true)}/panel/v1/moderation/possessions/${userId}/interventions/generate-from-scenario`,
        'POST',
        { scenario_id: Number(generatedScenarioId) },
      );
      await load();
    } catch (e: any) {
      setError(e?.message || 'Erreur génération intervention');
    }
  };




  return {
    handleDelete,
    callAction,
    startEdit,
    cancelEdit,
    handleSave,
    savePlayerProfile,
    adjustEconomy,
    toggleBuildingActive,
    updateBuildingCta,
    resetIntervention,
    cancelIntervention,
    generateFromScenario,
  };
}
